import { useCallback, useRef } from "react";
//# region redux
import { store } from "store";
import { useDispatch } from "react-redux";
import { openedCloseConfirmation } from "store/actions/diagnosticsActions";
//#endregion
//# region types
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { DiagnosticsEnums, DialogEnums } from "models/enums";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { SupportFormTypes } from "models/formTypes";
//# endregion
//# region components
import FAQPopperContent from "./_partials/faqPopperContent";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSupportService } from "serviceHooks";
import useFAQSupportFlowCloseConfirmation from "./useFAQSupportFlowCloseConfirmation";
import useSupportTicketCreateDialog from "./useCreateDialog";
import { useCloseConfirmation } from "components/common";
//# endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useSupportTicketCreateDialogWithFAQ(onActionSuccess?: () => void, onClose?: () => void): DialogProps {
  const currentUserMessageRef = useRef<string>("");
  const isSupportStepRef = useRef<boolean>(false);
  const supportFormValuesRef = useRef<SupportFormTypes.Create | undefined>(undefined);
  const dispatch = useDispatch();
  const supportService = useSupportService();
  const { getDialog: getSupportTicketCreateDialog } = useSupportTicketCreateDialog();

  const onSentSupportCase = () => {
    supportService.submitFAQSupportFlowDiagnostics(DiagnosticsEnums.ExitAction.SentSupportCase);
  };

  const { getCloseConfirmation: getFAQSupportFlowCloseConfirmation } = useFAQSupportFlowCloseConfirmation();
  const { showCustomConfirmation } = useCloseConfirmation();

  const handleSupportFormUpdate = useCallback((values: SupportFormTypes.Create) => {
    supportFormValuesRef.current = values;
  }, []);

  const closeDialog = async () => {
    isSupportStepRef.current = false;
    supportFormValuesRef.current = undefined;

    //TODO refactor to use useSelector instead - https://app.clickup.com/t/4523827/DEV-12514
    const exitAction = store.getState().diagnostics.faqDiagnostics.exitAction;
    if (exitAction === undefined)
      supportService.submitFAQSupportFlowDiagnostics(DiagnosticsEnums.ExitAction.ClosedDialog);
    else supportService.submitFAQSupportFlowDiagnostics();

    if (onClose) onClose();
  };

  const requestClose = (closeHandler?: (event?: React.MouseEvent<HTMLButtonElement>) => void) => {
    if (!isSuspectedAccidentalClose()) {
      if (closeHandler) return closeHandler();
      return closeDialog();
    }

    if (isSupportStepRef.current) {
      if (!supportService.supportFormHasContent(supportFormValuesRef.current)) {
        if (closeHandler) return closeHandler();
        return closeDialog();
      }

      return showCustomConfirmation(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FormExitWarningMessage),
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.UnansweredQuestionTextSupportStep),
        closeDialog,
        undefined
      );
    } else {
      const supportTicketCreateDialog = getSupportTicketCreateDialog(
        onSentSupportCase,
        () => supportService.submitFAQSupportFlowDiagnostics(DiagnosticsEnums.ExitAction.ClosedDialog),
        currentUserMessageRef.current,
        true
      );
      const { showConfirmation } = getFAQSupportFlowCloseConfirmation(supportTicketCreateDialog);
      dispatch(openedCloseConfirmation());

      return showConfirmation(closeDialog, async () => {
        if (onClose) onClose();
      });
    }
  };

  /**
   * @deprecated This function is deprecated. Use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Replace store.getState() calls with useSelector hooks
   * 2. Use the hooks in React components
   * 3. Handle state updates through React state management
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const isSuspected = isSuspectedAccidentalClose();
   *
   * @example
   * // New way (recommended):
   * const faqDiagnostics = useSelector((state: AppState) => state.diagnostics.faqDiagnostics);
   * const isSuspectedAccidentalClose = () => {
   *   return supportService.isSuspectedAccidentalClose(faqDiagnostics);
   * };
   */
  const isSuspectedAccidentalClose = () => {
    //TODO refactor to use useSelector instead - https://app.clickup.com/t/4523827/DEV-12514
    const faqDiagnostics = store.getState().diagnostics.faqDiagnostics;
    return supportService.isSuspectedAccidentalClose(faqDiagnostics);
  };

  return {
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NewMessage),
    titleIcon: IconDefinitionConstants.WCommentDots,
    dialogType: DialogEnums.DialogType.Simple,
    children: (
      <>
        <IconButton aria-label="close" className={cssExports.closeButton} onClick={() => requestClose()}>
          <CloseIcon />
        </IconButton>
        <FAQPopperContent
          closePopper={requestClose}
          setSubmitMessage={(currentMessage: string) => (currentUserMessageRef.current = currentMessage)}
          setIsSupportStep={(isSupportStep: boolean) => (isSupportStepRef.current = isSupportStep)}
          onSupportFormUpdate={handleSupportFormUpdate}
        />
      </>
    ),
    maxWidth: "xs",
    fullWidth: false,
    onSubmit: onActionSuccess,
    onClose: requestClose,
    className: cssExports.faqDialogContainer,
    hideSpeedDials: true,
    overrideCloseMenu: requestClose,
  };
}

export default useSupportTicketCreateDialogWithFAQ;
